import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck'
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen'
import {
  Box,
  type BoxProps,
  Flex,
  Indicator,
  Menu,
  Text,
  Tooltip,
} from '@mantine/core'
import { clsx } from 'clsx'
import { useEffect, useState } from 'react'

import { type CoreBookingDocumentCategory } from '@flecto/types'
import { Icon } from '@flecto/ui'

import { useBookingDocumentCategories } from '../../hooks/useBookingDocumentCategories'
import styles from './styles.module.css'

type SelectDocumentCategoriesProps = BoxProps & {
  value?: string | null
  onChange?: (cat: CoreBookingDocumentCategory) => void
  readOnly?: boolean
}

export function SelectDocumentCategories(props: SelectDocumentCategoriesProps) {
  const { categories, getCategory } = useBookingDocumentCategories()
  const [localValue, setLocalValue] =
    useState<CoreBookingDocumentCategory>('others')

  const categoriesValues = categories.map((cat) => cat.category)
  const selectedCategory = getCategory(localValue)

  const handleSelectItem = (cat: CoreBookingDocumentCategory) => {
    setLocalValue(cat)
    props.onChange?.(cat)
  }

  useEffect(() => {
    const propValue = props.value as CoreBookingDocumentCategory
    const newValue = categoriesValues.includes(propValue) ? propValue : 'others'

    setLocalValue(newValue)
  }, [props.value])

  const cssClasses = clsx(styles.select, { [styles.readOnly]: props.readOnly })

  const icon = selectedCategory?.icon && (
    <Icon
      icon={selectedCategory.icon}
      color={selectedCategory?.color}
      withCircleBackground
      size='md'
    />
  )

  return (
    <Box {...(props as BoxProps)} className={cssClasses}>
      <Menu
        position='bottom-start'
        disabled={props.readOnly}
        zIndex={11000}
        withinPortal
      >
        <Tooltip label={selectedCategory?.label} withinPortal>
          <Menu.Target
            data-testid={`item-selected-${selectedCategory?.category}`}
          >
            {!props.readOnly ? (
              <Indicator
                inline
                size={20}
                offset={5}
                position='bottom-end'
                color='white'
                label={<Icon icon={faPen} color='gray' size='xs' />}
              >
                {icon}
              </Indicator>
            ) : (
              icon
            )}
          </Menu.Target>
        </Tooltip>
        <Menu.Dropdown miw={200}>
          {categories.map((category) => {
            const isSelected = localValue === category.value
            const itemClasses = clsx({ [styles.itemActive]: isSelected })

            return (
              <Menu.Item
                className={itemClasses}
                key={category.category}
                data-testid={`item-${category.category}`}
                rightSection={
                  isSelected ? (
                    <Icon icon={faCheck} color='green' size='xs' />
                  ) : undefined
                }
                onClick={() => handleSelectItem(category.category)}
              >
                <Flex gap='sm' align='center'>
                  <Icon
                    icon={category.icon}
                    color={category.color}
                    size='sm'
                    withCircleBackground
                  />
                  <Text>{category.label}</Text>
                </Flex>
              </Menu.Item>
            )
          })}
        </Menu.Dropdown>
      </Menu>
    </Box>
  )
}
